import {
  ChannelPersistedConfigInput as ILandingPagesBuilderSchema,
  SectionInputFor,
} from '@playbook-technologies/common-schemas/lib/catalog';
import { defaultFeatures } from './OTPFeaturesSection';
import { toImgSrcRaw, toVideoSrcRaw } from './helpers';
import { defaultReviews, defaultFaqs } from './helpers';
import { defaultSubscriptionPageConfig } from '@playbook-technologies/common-schemas/lib/schemas/defaultSubCustomProgramPage';
import { removeAlpha as legacyRemoveAlpha } from '@playbook-technologies/common-schemas/lib/schemas/helpers';
import { loadPurchaseConfig } from './mapper';
import { OfferDetailsData } from './OfferDetails.mappers';
import {
  OfferDetailsSectionSchema,
  IntroductionSectionSchema,
} from './OTPLandingPage.schema';
import { ISection } from './schemas';
import { GetProgramPurchasePageByIdQuery } from '../../../generated/graphql';

type DefaultSubProgramInput = Parameters<
  typeof defaultSubscriptionPageConfig
>[0];

type ChannelSectionInput = SectionInputFor<'channel'>;

function inheritFromCreatorLanding<T extends ChannelSectionInput['type']>(
  landingPageConfig: ILandingPagesBuilderSchema | null,
  type: T,
  primaryColor: string,
) {
  const section = landingPageConfig?.sections.findLast(
    (s): s is Extract<ChannelSectionInput, { type: T }> => s.type === type,
  );
  if (!section) return undefined;
  const colors = [
    primaryColor,
    legacyRemoveAlpha(primaryColor, 0.1),
    '#FFFFFF',
  ];
  return {
    ...section,
    bgColor:
      section.colorIndex == null ? undefined : colors[section.colorIndex],
  };
}

export function defaultOTPPageConfig(
  rawProgram: GetProgramPurchasePageByIdQuery['program'] | null | undefined,
  {
    primaryColor,
    colors,
    landingPageConfig,
  }: {
    primaryColor: string;
    colors: string[];
    landingPageConfig: ILandingPagesBuilderSchema | null;
  },
) {
  const program = sanitizeProgram(rawProgram);

  return {
    sections: [
      {
        enabled: true,
        type: 'Introduction',
        title: program?.name || 'Program Title',
        subtitle: 'Program Subtitle',
        desc: program?.description || 'Program Description',
        coverImage: '',
        introVideo: toVideoSrcRaw(program?.mediaIntro),
        primaryColor,
        logo: landingPageConfig?.branding?.logo || '',
        colorIndex: 1,
        bgColor: colors[1],
        priceBefore: null,
        cta: {},
      },
      {
        type: 'EmailCapture',
        enabled: false,
        bgColor: colors[2],
        colorIndex: 2,
        title: '',
        description: '',
        displayAsPopup: true,
      },
      {
        enabled: true,
        colorIndex: 0,
        bgColor: colors[0],
        type: 'EmailCapture',
        title: '',
        description: '',
        image: null,
      },
      {
        enabled: true,
        type: 'ProgramStats',
        bgColor: colors[2],
        colorIndex: 2,
      },
      {
        enabled: true,
        type: 'TextSection1',
        title: 'About the program',
        desc: program?.description || 'Program description',
        coverImage: toImgSrcRaw(program?.mediaCover) || '',
        introVideo: toVideoSrcRaw(program?.mediaIntro),
        colorIndex: 2,
        bgColor: colors[2],
        cta: {},
      },
      {
        enabled: true,
        type: 'Workouts',
        title: 'Workouts',
        bgColor: colors[2],
        colorIndex: 2,
        workoutIds: [],
      },
      inheritFromCreatorLanding(landingPageConfig, 'Reviews', primaryColor) ?? {
        enabled: true,
        type: 'Reviews',
        title: 'Glowing 5-Star Reviews from Satisfied Customers',
        reviews: [...defaultReviews],
        bgColor: colors[1],
        colorIndex: 1,
      },
      inheritFromCreatorLanding(
        landingPageConfig,
        'Testimonials',
        primaryColor,
      ) ?? {
        enabled: true,
        type: 'Testimonials',
        title: 'Need to see it to believe?',
        testimonials: program ? parseProgramTestimonials(program) : [],
        bgColor: colors[0],
        colorIndex: 0,
      },
      {
        enabled: true,
        type: 'OTPFeatures',
        title: 'Program Features',
        bgColor: colors[1],
        colorIndex: 1,
        features: defaultFeatures,
      },
      inheritFromCreatorLanding(landingPageConfig, 'Bio', primaryColor) ?? {
        enabled: true,
        type: 'Bio',
        title: program?.influencer?.bio || 'Online personal trainer',
        desc: program?.influencer?.messageProfile || '',
        coverImage: toImgSrcRaw(program?.influencer?.mediaAvatar),
        bgColor: colors[0],
        colorIndex: 0,
        introVideo: null,
      },
      {
        enabled: true,
        type: 'TextSection2',
        title: 'Get support & accountability from our amazing community',
        desc:
          program?.influencer?.messageCommunity ||
          `Find encouragement and accountability by connecting with others in our exclusive private community. There you can learn about nutrition, learn more about form, and connect with other members.${
            program?.influencer?.firstName
              ? ` Bonus: ${program.influencer.firstName} will be right there to cheer on you.`
              : ''
          }`,
        images: [{ url: null }, { url: null }],
        bgColor: colors[0],
        colorIndex: 0,
        cta: {},
      },
      {
        enabled: true,
        type: 'TextSection2',
        title: 'Bonus material',
        desc: 'Additional bonus content',
        bullets: [],
        images: [{ url: null }, { url: null }],
        rotateImages: true,
        bgColor: colors[1],
        colorIndex: 1,
        cta: {},
      },
      inheritFromCreatorLanding(landingPageConfig, 'FAQs', primaryColor) ?? {
        enabled: true,
        type: 'FAQs',
        title: 'Frequently asked questions',
        faqs: [...(program?.faq?.length ? program.faq : defaultFaqs)],
        bgColor: colors[2],
        colorIndex: 2,
      },
    ],
  };
}

export function loadOfferPageConfig(
  program: GetProgramPurchasePageByIdQuery['program'] | null | undefined,
  serverConfig: { sections?: ISection[] } | null,
  landingPageConfig: ILandingPagesBuilderSchema | null,
  { primaryColor, colors }: { primaryColor: string; colors: string[] },
  offerData?: OfferDetailsData,
  offerMeta?: { title?: string | null; description?: string | null },
): { sections: ISection[] } {
  // If serverConfig exists, use it (existing saved config)
  if (serverConfig?.sections) {
    return loadPurchaseConfig({
      ...serverConfig,
      sections: serverConfig.sections,
    });
  }

  // Create Introduction section for offer mode
  // Use creator/landing page data as fallback since program is not available
  const offerName = offerMeta?.title?.trim() ? offerMeta.title.trim() : null;

  const description =
    typeof offerMeta?.description === 'string'
      ? offerMeta.description.trim()
      : null;

  const introSection: IntroductionSectionSchema = {
    enabled: true,
    type: 'Introduction' as const,
    title: offerName || 'Offer Title',
    subtitle: 'Welcome to my offer',
    desc: description && description.length > 0 ? description : '',
    coverImage: '',
    introVideo: null,
    primaryColor,
    logo: landingPageConfig?.branding?.logo || '',
    colorIndex: 1,
    bgColor: colors[1],
    priceBefore: null,
    cta: {},
  };

  // Create typed OfferDetails section with defaults
  const offerDetailsSection: OfferDetailsSectionSchema = {
    enabled: true,
    type: 'OfferDetails',
    headlineText: offerName ?? "What's included?",
    programIds: offerData?.programIds.options.map((opt) => opt.value) || [],
    workoutsPreviewEnabled: false,
    workoutIds: [],
    extrasIds: offerData?.extrasIds.options.map((opt) => opt.value) || [],
    communityFeatures:
      offerData?.communityFeatures.options.map((opt) => opt.value) || [],
    bgColor: colors[2],
    colorIndex: 2,
  };

  // Default config: only Introduction + OfferDetails
  const sections: ISection[] = [introSection, offerDetailsSection];

  return loadPurchaseConfig({ sections });
}

export function loadProgramPageConfig(
  rawProgram: GetProgramPurchasePageByIdQuery['program'] | null | undefined,
  serverConfig,
  landingPageConfig,
  { primaryColor, colors },
  options?: {
    isOfferMode?: boolean;
    offerData?: OfferDetailsData;
    offerMetadata?: { title?: string | null; description?: string | null };
  },
) {
  const program = sanitizeProgram(rawProgram);

  // Offer landing page mode
  if (options?.isOfferMode) {
    return loadOfferPageConfig(
      program,
      serverConfig,
      landingPageConfig,
      { primaryColor, colors },
      options.offerData,
      options.offerMetadata,
    );
  }

  // If serverConfig exists, use it (existing saved config)
  if (serverConfig?.sections) {
    return loadPurchaseConfig({
      ...serverConfig,
      sections: serverConfig.sections,
    });
  }

  // Existing OTP/Subscription logic (unchanged)
  const isSubscription = program?.accessType === 'premium';
  const defaultConfig =
    isSubscription && program
      ? defaultSubscriptionPageConfig(program as DefaultSubProgramInput, {
          mainColor: primaryColor,
          landingPageConfig,
        })
      : defaultOTPPageConfig(program, {
          primaryColor,
          colors,
          landingPageConfig,
        });
  const res = loadPurchaseConfig({
    ...serverConfig,
    sections: defaultConfig.sections,
  });
  return res;
}

function parseProgramTestimonials(program) {
  if (!program?.testimonials) return [];

  return program.testimonials.map((testimonial) => {
    return {
      name: testimonial.name,
      text: testimonial.text,
      image: toImgSrcRaw(testimonial.media),
      video: toVideoSrcRaw(testimonial.media),
    };
  });
}

function sanitizeProgram(
  program: GetProgramPurchasePageByIdQuery['program'] | null | undefined,
) {
  if (!program) return program;

  const testimonials =
    program.testimonials
      ?.filter(
        (
          testimonial,
        ): testimonial is NonNullable<typeof program.testimonials>[number] => {
          if (!testimonial?.media) return false;
          const { cdn01, image } = testimonial.media;
          return Boolean(cdn01 && image);
        },
      )
      .map((testimonial) => ({
        ...testimonial,
        media: {
          ...testimonial.media,
        },
      })) ?? [];

  return {
    ...program,
    testimonials,
  };
}
